import { Label } from "../../primitives/label";
import { Input } from "../../primitives/input";
import { Textarea } from "../../primitives/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../primitives/select";
import { Checkbox } from "../../primitives/checkbox";
import { AlertCircle, Sparkles, Loader2 } from "lucide-react";
import { cn } from "../../primitives/utils";
import { Button } from "../../primitives/button";
import { CollapsibleSection } from "../../patterns/CollapsibleSection";
import {
  MultiExpressionEditor,
  ExpressionField,
} from "./MultiExpressionEditor";
import { AutocompleteField } from "./ExpressionEditor";

// Type definitions
type DefinitionType = "signal" | "alert" | "opportunity" | "recommendation";
type DefinitionStatus = "live" | "inactive" | "draft";

interface ValidationError {
  field: string;
  message: string;
}

interface DatasetOption {
  id: string;
  name: string;
}

interface FamilyOption {
  id: string;
  name: string;
}

interface VersionOption {
  version: number;
  timestamp: string;
}

interface ConfigDefinitionEditorProps {
  type: DefinitionType;
  name: string;
  family: string;
  description?: string;
  expressionFields: ExpressionField[];
  status: DefinitionStatus;
  version: number;
  selectedDatasets?: string[];
  familyOptions: FamilyOption[];
  datasetOptions?: DatasetOption[];
  versionOptions?: VersionOption[];
  validationErrors?: ValidationError[];
  savedExpressionFields?: ExpressionField[]; // Last saved fields for existing rules
  validStatusOptions?: DefinitionStatus[]; // NEW: Filter status dropdown options
  onChange: (field: string, value: any) => void;
  onExpressionFieldChange: (key: string, value: string) => void;
  onRevertAllExpressions?: () => void;
  onVersionChange?: (version: number) => void;
  /**
   * When provided, renders a small "Generate" / "Regenerate" button next to
   * the Description label. The parent owns the assistant panel and decides
   * what that button does (usually: open the assistant and send a canned
   * description-generation prompt).
   */
  onRegenerateDescription?: () => void;
  /** Shown while the assistant is producing a description. */
  isRegeneratingDescription?: boolean;
}

// Mock autocomplete data - in real app this would come from API
const getMockAutocompleteFields = (
  datasets: string[],
): Record<string, AutocompleteField[]> => {
  const allFields: Record<string, AutocompleteField[]> = {
    usage_metrics: [
      { name: "login_count", type: "integer", description: "Number of logins" },
      {
        name: "session_duration",
        type: "float",
        description: "Average session duration",
      },
      {
        name: "last_login_date",
        type: "date",
        description: "Last login timestamp",
      },
      {
        name: "days_since_last_login",
        type: "integer",
        description: "Days since last login",
      },
      {
        name: "feature_usage_score",
        type: "float",
        description: "Feature adoption score",
      },
    ],
    billing: [
      { name: "mrr", type: "float", description: "Monthly recurring revenue" },
      { name: "plan_tier", type: "string", description: "Current plan tier" },
      { name: "payment_status", type: "string", description: "Payment status" },
      {
        name: "days_until_renewal",
        type: "integer",
        description: "Days until renewal",
      },
    ],
    orders: [
      {
        name: "order_count_last_90_days",
        type: "integer",
        description: "Orders in last 90 days",
      },
      {
        name: "order_count_previous_90_days",
        type: "integer",
        description: "Orders in previous 90 days",
      },
      {
        name: "avg_order_value",
        type: "float",
        description: "Average order value",
      },
      {
        name: "total_order_value",
        type: "float",
        description: "Total order value",
      },
    ],
    support_tickets: [
      {
        name: "ticket_count",
        type: "integer",
        description: "Number of tickets",
      },
      {
        name: "avg_resolution_time",
        type: "float",
        description: "Average resolution time",
      },
      {
        name: "satisfaction_score",
        type: "float",
        description: "Support satisfaction score",
      },
    ],
  };

  const result: Record<string, AutocompleteField[]> = {};
  datasets.forEach((dataset) => {
    if (allFields[dataset]) {
      result[dataset] = allFields[dataset];
    }
  });

  return result;
};

const getMockSignalFields = (): AutocompleteField[] => {
  return [
    {
      name: "churn_risk_score",
      type: "float",
      description: "Churn risk score",
    },
    {
      name: "health_score",
      type: "integer",
      description: "Customer health score",
    },
    {
      name: "engagement_trend",
      type: "string",
      description: "Engagement trend",
    },
    {
      name: "low_engagement",
      type: "boolean",
      description: "Low engagement flag",
    },
  ];
};

export function ConfigDefinitionEditor({
  type,
  name,
  family,
  description,
  expressionFields,
  status,
  version,
  selectedDatasets = [],
  familyOptions,
  datasetOptions = [],
  versionOptions = [],
  validationErrors = [],
  savedExpressionFields, // Last saved fields for existing rules
  validStatusOptions, // NEW: Filter status dropdown options
  onChange,
  onExpressionFieldChange,
  onRevertAllExpressions,
  onVersionChange,
  onRegenerateDescription,
  isRegeneratingDescription = false,
}: ConfigDefinitionEditorProps) {
  // Determine if datasets should be shown
  const showDatasetsSection = type === "signal" || type === "alert";
  const datasetsRequired = type === "signal";

  // Helper to get validation error for a field
  const getFieldError = (fieldName: string) => {
    return validationErrors.find((e) => e.field === fieldName);
  };

  // Handle dataset toggle
  const handleDatasetToggle = (datasetId: string) => {
    const newSelection = selectedDatasets.includes(datasetId)
      ? selectedDatasets.filter((id) => id !== datasetId)
      : [...selectedDatasets, datasetId];
    onChange("selectedDatasets", newSelection);
  };

  // Get available fields for reference panel
  const getAvailableFieldsList = () => {
    const fieldsList: { label: string; type: string; description?: string }[] =
      [];

    // Add dataset-specific fields if applicable
    if (
      (type === "signal" || type === "alert") &&
      selectedDatasets.length > 0
    ) {
      const datasetFields = getMockAutocompleteFields(selectedDatasets);
      Object.entries(datasetFields).forEach(
        ([datasetId, datasetFieldArray]) => {
          datasetFieldArray.forEach((field) => {
            fieldsList.push({
              label: `${datasetId}.${field.name}`,
              type: field.type,
              description: field.description,
            });
          });
        },
      );
    }

    // Add signal fields if applicable
    if (
      type === "alert" ||
      type === "opportunity" ||
      type === "recommendation"
    ) {
      const signalFields = getMockSignalFields();
      signalFields.forEach((field) => {
        fieldsList.push({
          label: `signals.${field.name}`,
          type: field.type,
          description: field.description,
        });
      });
    }

    return fieldsList;
  };

  const nameError = getFieldError("name");
  const familyError = getFieldError("family");
  const expressionError = getFieldError("expression");
  const datasetsError = getFieldError("datasets");

  const availableFields = getAvailableFieldsList();

  return (
    <div className="space-y-6">
      {/* Name Field */}
      <div className="space-y-2">
        <Label htmlFor="def-name">Name</Label>
        <Input
          id="def-name"
          value={name}
          onChange={(e) => onChange("name", e.target.value)}
          placeholder="Enter definition name"
          aria-invalid={!!nameError}
        />
        {nameError && (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="size-4" />
            <span>{nameError.message}</span>
          </div>
        )}
      </div>

      {/* Description Field */}
      {description !== undefined && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label htmlFor="def-description">Description</Label>
            {onRegenerateDescription && (
              <Button
                type="button"
                size="sm"
                variant="ghost"
                className="h-6 text-xs"
                onClick={onRegenerateDescription}
                disabled={isRegeneratingDescription}
              >
                {isRegeneratingDescription ? (
                  <>
                    <Loader2 className="size-3 mr-1 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="size-3 mr-1" />
                    {description.trim() ? "Regenerate" : "Generate"}
                  </>
                )}
              </Button>
            )}
          </div>
          <Textarea
            id="def-description"
            value={description}
            onChange={(e) => onChange("description", e.target.value)}
            placeholder="Human-readable description of what this definition does"
            rows={2}
            className="resize-none"
          />
        </div>
      )}

      {/* Family Field */}
      <div className="space-y-2">
        <Label htmlFor="def-family">Family</Label>
        <Select
          value={family}
          onValueChange={(value) => onChange("family", value)}
        >
          <SelectTrigger id="def-family" aria-invalid={!!familyError}>
            <SelectValue placeholder="Select family" />
          </SelectTrigger>
          <SelectContent>
            {familyOptions.map((option) => (
              <SelectItem key={option.id} value={option.id}>
                {option.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {familyError && (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="size-4" />
            <span>{familyError.message}</span>
          </div>
        )}
      </div>

      {/* Datasets Field (conditional) */}
      {showDatasetsSection && (
        <div className="space-y-2">
          <CollapsibleSection
            title={
              <>
                Datasets{" "}
                {datasetsRequired && (
                  <span className="text-destructive">*</span>
                )}
              </>
            }
            defaultOpen
          >
            <div className="space-y-3">
              {datasetOptions.length > 0 ? (
                datasetOptions.map((dataset) => (
                  <div key={dataset.id} className="flex items-center gap-2">
                    <Checkbox
                      id={`dataset-${dataset.id}`}
                      checked={selectedDatasets.includes(dataset.id)}
                      onCheckedChange={() => handleDatasetToggle(dataset.id)}
                    />
                    <Label
                      htmlFor={`dataset-${dataset.id}`}
                      className="cursor-pointer"
                    >
                      {dataset.name}
                    </Label>
                  </div>
                ))
              ) : (
                <p className="text-sm text-muted-foreground">
                  No datasets available
                </p>
              )}
            </div>
          </CollapsibleSection>
          {datasetsError && (
            <div className="flex items-center gap-2 text-sm text-destructive">
              <AlertCircle className="size-4" />
              <span>{datasetsError.message}</span>
            </div>
          )}
          {type === "alert" && (
            <p className="text-xs text-muted-foreground">
              All signals are automatically available. Datasets are optional.
            </p>
          )}
        </div>
      )}

      {/* Available Fields Reference */}
      {availableFields.length > 0 && (
        <CollapsibleSection title="Available Fields">
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {availableFields.map((field, idx) => (
              <div key={idx} className="flex items-start gap-2 text-xs">
                <code className="bg-muted px-1.5 py-0.5 rounded font-mono text-foreground flex-shrink-0">
                  {field.label}
                </code>
                <span className="text-muted-foreground flex-shrink-0">
                  ({field.type})
                </span>
                {field.description && (
                  <span className="text-muted-foreground">
                    {field.description}
                  </span>
                )}
              </div>
            ))}
          </div>
        </CollapsibleSection>
      )}

      {/* Expression Fields with Multi-Expression Editor */}
      <div className="space-y-2">
        <Label htmlFor="def-expression">Expressions</Label>
        <p className="text-xs text-muted-foreground">
          Each field is computed independently. Click a row to expand and edit
          that expression. Press Tab to autocomplete fields and keywords.
        </p>
        <div
          className={cn(
            expressionError && "ring-2 ring-destructive rounded-md",
          )}
        >
          <MultiExpressionEditor
            type={type}
            fields={expressionFields}
            onChange={onExpressionFieldChange}
            selectedDatasets={selectedDatasets}
            availableDatasets={getMockAutocompleteFields(selectedDatasets)}
            availableSignals={getMockSignalFields()}
            originalFields={savedExpressionFields}
            onRevertAll={onRevertAllExpressions}
          />
        </div>
        {expressionError && (
          <div className="flex items-center gap-2 text-sm text-destructive">
            <AlertCircle className="size-4" />
            <span>{expressionError.message}</span>
          </div>
        )}
      </div>

      {/* Status and Version Row */}
      <div className="grid grid-cols-2 gap-4">
        {/* Status Field */}
        <div className="space-y-2">
          <Label htmlFor="def-status">Status</Label>
          <Select
            value={status}
            onValueChange={(value) => onChange("status", value)}
          >
            <SelectTrigger id="def-status">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {validStatusOptions ? (
                validStatusOptions.map((option) => (
                  <SelectItem key={option} value={option}>
                    {option.charAt(0).toUpperCase() + option.slice(1)}
                  </SelectItem>
                ))
              ) : (
                <>
                  <SelectItem value="live">Live</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                  <SelectItem value="draft">Draft</SelectItem>
                </>
              )}
            </SelectContent>
          </Select>
        </div>

        {/* Version Field */}
        <div className="space-y-2">
          <Label htmlFor="def-version">Version</Label>
          {versionOptions.length > 0 && onVersionChange ? (
            <Select
              value={version.toString()}
              onValueChange={(value) => onVersionChange(parseInt(value))}
            >
              <SelectTrigger id="def-version">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {versionOptions.map((v) => (
                  <SelectItem key={v.version} value={v.version.toString()}>
                    v{v.version} ({v.timestamp})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="h-9 flex items-center px-3 border rounded-md bg-input-background text-sm text-muted-foreground">
              {version > 0 ? `v${version}` : "(new version on save)"}
            </div>
          )}
        </div>
      </div>

      {/* General validation errors */}
      {validationErrors
        .filter(
          (e) =>
            !["name", "family", "expression", "datasets"].includes(e.field),
        )
        .map((error, idx) => (
          <div
            key={idx}
            className="flex items-center gap-2 text-sm text-destructive"
          >
            <AlertCircle className="size-4" />
            <span>
              {error.field}: {error.message}
            </span>
          </div>
        ))}
    </div>
  );
}
